import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import { handleDiscoveryCallClick } from '@/components/layout/Navbar';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { SocialShare } from '@/components/ui/SocialShare';
import { CheckCircle2, Info, Activity } from 'lucide-react';
import { ScrollReveal } from '@/components/ui/ScrollReveal';
import { AnimatedCounter } from '@/components/ui/AnimatedCounter';
import { AssessmentProgressTracker } from '@/components/ui/AssessmentProgressTracker';
import { LazyImage } from '@/components/ui/LazyImage';

export const TeamEffectivenessPage = () => {
  const { links } = useSiteSettings();
  return (
    <main className="pt-32 pb-20 bg-ground-alt min-h-screen">
      <SocialShare title="Enneagram Team Building Workshops | FIVE LENS" />
      
      <div className="max-w-6xl mx-auto px-6 lg:px-8 mb-8">
        <Breadcrumb items={[{ label: 'For Organizations & HR Teams', href: '/organizations' }, { label: 'Team Effectiveness' }]} />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 mb-20">
        <ScrollReveal>
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
            <div>
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-rule-3 mb-4 block">Team Effectiveness</span>
              <h1 className="text-4xl lg:text-5xl font-serif text-ink mb-6">
                Stop treating symptoms. Resolve the root cause of team friction.
              </h1>
              <p className="text-xl text-ink/80 mb-8 leading-relaxed">
                For organizations experiencing operational drag due to misaligned leadership or entrenched silos. Team building exercises don't fix systemic behavioral patterns. We provide the data-driven depth required to build high-trust, high-performance environments.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  'Comprehensive mapping of team dynamics and blind spots',
                  'Identification of root causes for entrenched conflict',
                  'Actionable frameworks for trust and psychological safety',
                  'Customized alignment interventions based on live data'
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-rule-3 shrink-0 mt-0.5" />
                    <span className="text-ink/80">{item}</span>
                  </div>
                ))}
              </div>

              <a 
                href={links.bookingUrl} 
                onClick={() => handleDiscoveryCallClick(links.bookingUrl)}
                className="inline-flex bg-ink text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-rule-3 transition-colors"
              >
                Book a Discovery Call
              </a>
            </div>
            
            <div className="bg-white p-2 border border-ground-deep relative">
              <LazyImage 
                src="/five_lens_team_effectiveness_hero_web.webp" 
                alt="Executive team in a modern office working together on team alignment and collaboration" 
                className="w-full h-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </ScrollReveal>
      </div>

      <section className="bg-white py-24 border-y border-ground-deep">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-serif text-ink mb-6">The True Cost of Dysfunctional Teams</h2>
          <p className="text-lg text-ink/70 leading-relaxed">
            When executive and cross-functional teams fail to cohere, the entire organization pays the price. Our Team Effectiveness program is designed to resolve the deep, underlying issues that cause systemic drag.
          </p>
        </div>

        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-8 text-left mb-24">
            <div className="p-8 border border-ground-deep hover:border-rule-3 transition-colors">
              <h3 className="text-xl font-serif text-ink mb-3">Siloed Operations</h3>
              <p className="text-sm text-ink/70">
                Departments become protective and territorial, withholding information and resources instead of collaborating toward shared organizational goals.
              </p>
            </div>
            <div className="p-8 border border-ground-deep hover:border-rule-3 transition-colors">
              <h3 className="text-xl font-serif text-ink mb-3">Decision Paralysis</h3>
              <p className="text-sm text-ink/70">
                A lack of trust and psychological safety prevents healthy conflict, leading to artificial harmony, passive-aggressive behaviors, and painfully slow execution.
              </p>
            </div>
            <div className="p-8 border border-ground-deep hover:border-rule-3 transition-colors">
              <h3 className="text-xl font-serif text-ink mb-3">Leadership Attrition</h3>
              <p className="text-sm text-ink/70">
                Toxic team dynamics drive high-performing talent out the door. The emotional tax of navigating constant friction leads to severe burnout and resignation.
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl font-serif text-ink mb-8">The Engagement Sequence</h2>
              <p className="text-ink/70/80 leading-relaxed mb-10">
                We utilize a structured, two-phased approach to transform team dynamics. By leveraging the data from the FIVE LENS assessments, we move beyond generic team-building to highly targeted, behavioral alignment.
              </p>
              
              <div className="space-y-8">
                <div className="flex items-start gap-6 bg-ground-alt p-8 border border-ground-deep">
                  <div className="w-12 h-12 shrink-0 bg-ink text-white flex items-center justify-center font-serif text-2xl shadow-md">1</div>
                  <div>
                    <h4 className="text-xl font-bold text-ink mb-2">Team Discovery Day (TDD)</h4>
                    <p className="text-ink/70/70 text-sm leading-relaxed mb-4">
                      A high-impact, one-day workshop where the team's collective behavioral patterns are revealed. We map the entire team across all FIVE LENSes, making unconscious dynamics visible and objective.
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2 text-sm text-ink/70/80"><CheckCircle2 className="w-4 h-4 text-rule-3 mt-0.5 shrink-0" /> Aggregate Team Profile Analysis</li>
                      <li className="flex items-start gap-2 text-sm text-ink/70/80"><CheckCircle2 className="w-4 h-4 text-rule-3 mt-0.5 shrink-0" /> Identification of Collective Blind Spots</li>
                      <li className="flex items-start gap-2 text-sm text-ink/70/80"><CheckCircle2 className="w-4 h-4 text-rule-3 mt-0.5 shrink-0" /> Establishing a Common Behavioral Language</li>
                    </ul>
                  </div>
                </div>
                
                <div className="flex items-start gap-6 bg-ground-alt p-8 border border-ground-deep">
                  <div className="w-12 h-12 shrink-0 bg-ink text-white flex items-center justify-center font-serif text-2xl shadow-md">2</div>
                  <div>
                    <h4 className="text-xl font-bold text-ink mb-2">Team Performance Process (TPP)</h4>
                    <p className="text-ink/70/70 text-sm leading-relaxed mb-4">
                      A sustained series of targeted interventions following the TDD. Based on the specific friction points identified, our facilitators guide the team through a custom curriculum to embed new behaviors and rebuild trust.
                    </p>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-start gap-2 text-sm text-ink/70/80"><CheckCircle2 className="w-4 h-4 text-rule-3 mt-0.5 shrink-0" /> Multi-Session Facilitated Interventions</li>
                      <li className="flex items-start gap-2 text-sm text-ink/70/80"><CheckCircle2 className="w-4 h-4 text-rule-3 mt-0.5 shrink-0" /> Conflict Resolution & Trust Building</li>
                      <li className="flex items-start gap-2 text-sm text-ink/70/80"><CheckCircle2 className="w-4 h-4 text-rule-3 mt-0.5 shrink-0" /> Strategic Alignment & Action Planning</li>
                    </ul>
                    <a href="#tpp-framework" className="inline-block border border-rule-3 text-rule-3 text-xs font-bold uppercase tracking-widest px-4 py-2 hover:bg-rule-3 hover:text-white transition-colors">
                      Explore the 6 Essential Skills
                    </a>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-ink text-white p-10 relative mt-16 md:mt-0">
              <h3 className="text-2xl font-serif mb-8 text-white">Measurable Program Outcomes</h3>
              <div className="space-y-8">
                <div>
                  <h4 className="font-bold text-rule-3 uppercase tracking-widest text-xs mb-2">Elevated Psychological Safety</h4>
                  <p className="text-sm text-white/90 leading-relaxed">Teams create environments where robust debate is encouraged, mistakes are leveraged for learning, and diverse perspectives are actively sought out.</p>
                </div>
                <div className="h-px w-full bg-white/10"></div>
                <div>
                  <h4 className="font-bold text-rule-3 uppercase tracking-widest text-xs mb-2">Accelerated Execution</h4>
                  <p className="text-sm text-white/90 leading-relaxed">By removing the emotional friction caused by unresolved conflict and political maneuvering, teams make decisions faster and implement them with unified commitment.</p>
                </div>
                <div className="h-px w-full bg-white/10"></div>
                <div>
                  <h4 className="font-bold text-rule-3 uppercase tracking-widest text-xs mb-2">Systemic Resilience</h4>
                  <p className="text-sm text-white/90 leading-relaxed">The team develops a collective capacity to absorb market shocks and organizational change without fracturing into defensive silos or blame cycles.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-ground-alt py-24 border-b border-ground-deep">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <ScrollReveal>
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-rule-3 mb-4 block flex items-center gap-2">
                  <Activity className="w-4 h-4" />
                  Real-time Visibility
                </span>
                <h2 className="text-3xl lg:text-4xl font-serif text-ink mb-6">
                  Track organizational readiness in real-time.
                </h2>
                <p className="text-xl text-ink/80 mb-8 leading-relaxed">
                  Gain immediate visibility into assessment completion rates across your organization. Monitor which departments are ready for the Team Discovery Day and which need follow-ups before the engagement begins.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 bg-white border border-ground-deep">
                    <CheckCircle2 className="w-6 h-6 text-rule-3 shrink-0" />
                    <div>
                      <h4 className="font-bold text-ink mb-1">Live Tracking</h4>
                      <p className="text-sm text-ink/70">Monitor progress as individuals complete their FIVE LENS diagnostics.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 bg-white border border-ground-deep">
                    <CheckCircle2 className="w-6 h-6 text-rule-3 shrink-0" />
                    <div>
                      <h4 className="font-bold text-ink mb-1">Automated Follow-ups</h4>
                      <p className="text-sm text-ink/70">Trigger reminder emails directly from the portal to keep engagements on schedule.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="relative">
                <div className="absolute inset-0 bg-rule-3/10 translate-x-4 translate-y-4"></div>
                <div className="relative z-10">
                  <AssessmentProgressTracker />
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="bg-gradient-to-br from-ink to-ink/80 py-24 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center mb-16">
          <ScrollReveal>
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-rule-3 mb-4 block">Proven Impact</span>
            <h2 className="text-3xl lg:text-4xl font-serif mb-6 text-white">Measurable Results</h2>
            <p className="text-lg text-white max-w-2xl mx-auto">
              Organizations that undergo the FIVE LENS Team Effectiveness engagement see dramatic shifts in collaboration, execution, and psychological safety.
            </p>
          </ScrollReveal>
        </div>

        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-12 text-center">
            <ScrollReveal delay={0.1}>
              <div className="p-8 border border-white/10 bg-white/5 hover:bg-white/10 transition-colors h-full flex flex-col items-center justify-center">
                <div className="text-5xl lg:text-6xl font-serif text-rule-3 mb-4 relative inline-flex items-start justify-center group cursor-help">
                  <AnimatedCounter end={42} suffix="%" duration={2000} />
                  <Info className="w-5 h-5 ml-2 mt-2 text-white/40 group-hover:text-rule-3 transition-colors" />
                  
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-4 w-60 p-4 bg-white rounded-xl shadow-2xl opacity-0 group-hover:opacity-100 transition-all pointer-events-none z-20 translate-y-2 group-hover:translate-y-0">
                    <p className="text-sm font-sans text-ink leading-relaxed text-center normal-case tracking-normal font-normal">
                      Measured via pre- and post-engagement cross-functional feedback surveys.
                    </p>
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-white"></div>
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-2">Improved Collaboration</h3>
                <p className="text-sm text-white/60">Reduction in siloed operations and inter-departmental friction within 6 months.</p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={0.2}>
              <div className="p-8 border border-white/10 bg-white/5 hover:bg-white/10 transition-colors h-full flex flex-col items-center justify-center">
                <div className="text-5xl lg:text-6xl font-serif text-rule-3 mb-4 relative inline-flex items-start justify-center group cursor-help">
                  <AnimatedCounter end={65} suffix="%" duration={2000} />
                  <Info className="w-5 h-5 ml-2 mt-2 text-white/40 group-hover:text-rule-3 transition-colors" />
                  
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-4 w-60 p-4 bg-white rounded-xl shadow-2xl opacity-0 group-hover:opacity-100 transition-all pointer-events-none z-20 translate-y-2 group-hover:translate-y-0">
                    <p className="text-sm font-sans text-ink leading-relaxed text-center normal-case tracking-normal font-normal">
                      Calculated by tracking project cycle times and meeting efficiency before and after intervention.
                    </p>
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-white"></div>
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-2">Faster Decision Making</h3>
                <p className="text-sm text-white/60">Increase in strategic execution speed due to enhanced psychological safety.</p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.3}>
              <div className="p-8 border border-white/10 bg-white/5 hover:bg-white/10 transition-colors h-full flex flex-col items-center justify-center">
                <div className="text-5xl lg:text-6xl font-serif text-rule-3 mb-4 relative inline-flex items-start justify-center group cursor-help">
                  <AnimatedCounter end={30} suffix="%" duration={2000} />
                  <Info className="w-5 h-5 ml-2 mt-2 text-white/40 group-hover:text-rule-3 transition-colors" />
                  
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-4 w-60 p-4 bg-white rounded-xl shadow-2xl opacity-0 group-hover:opacity-100 transition-all pointer-events-none z-20 translate-y-2 group-hover:translate-y-0">
                    <p className="text-sm font-sans text-ink leading-relaxed text-center normal-case tracking-normal font-normal">
                      Based on 12-month retention rates compared to industry benchmarks for executive talent.
                    </p>
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-white"></div>
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-2">Reduced Attrition</h3>
                <p className="text-sm text-white/60">Decrease in voluntary turnover among high-performing leadership talent.</p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <section id="tpp-framework" className="bg-ground py-24 border-b border-ground-deep">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-rule-3 mb-4 block">The TPP Framework</span>
            <h2 className="text-3xl lg:text-4xl font-serif text-ink mb-6">The 6 Essential Team Skills</h2>
            <p className="text-lg text-ink/70/80 leading-relaxed">
              Our Team Performance Process (TPP) is anchored in developing six core competencies. By diagnosing gaps in these areas, we can implement precise interventions that drive a sustainable, high-performance culture.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 space-y-6">
              <div className="bg-white p-6 border border-ground-deep flex gap-4 hover:border-rule-3 transition-colors group">
                <div className="w-10 h-10 shrink-0 bg-ink text-white flex items-center justify-center font-serif text-xl group-hover:bg-rule-3 transition-colors">1</div>
                <div>
                  <h4 className="text-lg font-bold text-ink mb-1">Giving & Receiving Feedback</h4>
                  <p className="text-sm text-ink/70/70">Develop a shared feedback culture grounded in respect, clarity, and awareness.</p>
                </div>
              </div>
              
              <div className="bg-white p-6 border border-ground-deep flex gap-4 hover:border-rule-3 transition-colors group">
                <div className="w-10 h-10 shrink-0 bg-ink text-white flex items-center justify-center font-serif text-xl group-hover:bg-rule-3 transition-colors">2</div>
                <div>
                  <h4 className="text-lg font-bold text-ink mb-1">Managing Conflict</h4>
                  <p className="text-sm text-ink/70/70">Transform conflict into constructive dialogue that strengthens relationships.</p>
                </div>
              </div>
              
              <div className="bg-white p-6 border border-ground-deep flex gap-4 hover:border-rule-3 transition-colors group">
                <div className="w-10 h-10 shrink-0 bg-ink text-white flex items-center justify-center font-serif text-xl group-hover:bg-rule-3 transition-colors">3</div>
                <div>
                  <h4 className="text-lg font-bold text-ink mb-1">Building Trust & Common Values</h4>
                  <p className="text-sm text-ink/70/70">Build psychological safety and align on the values that guide team behaviour.</p>
                </div>
              </div>
              
              <div className="bg-white p-6 border border-ground-deep flex gap-4 hover:border-rule-3 transition-colors group">
                <div className="w-10 h-10 shrink-0 bg-ink text-white flex items-center justify-center font-serif text-xl group-hover:bg-rule-3 transition-colors">4</div>
                <div>
                  <h4 className="text-lg font-bold text-ink mb-1">Thinking Partners</h4>
                  <p className="text-sm text-ink/70/70">Learn to practise peer coaching to deepen accountability, learning, and support.</p>
                </div>
              </div>

              <div className="bg-white p-6 border border-ground-deep flex gap-4 hover:border-rule-3 transition-colors group">
                <div className="w-10 h-10 shrink-0 bg-ink text-white flex items-center justify-center font-serif text-xl group-hover:bg-rule-3 transition-colors">5</div>
                <div>
                  <h4 className="text-lg font-bold text-ink mb-1">Collaborating Effectively</h4>
                  <p className="text-sm text-ink/70/70">Solve challenges together using structured, inclusive methods.</p>
                </div>
              </div>

              <div className="bg-white p-6 border border-ground-deep flex gap-4 hover:border-rule-3 transition-colors group">
                <div className="w-10 h-10 shrink-0 bg-ink text-white flex items-center justify-center font-serif text-xl group-hover:bg-rule-3 transition-colors">6</div>
                <div>
                  <h4 className="text-lg font-bold text-ink mb-1">Building Resilience</h4>
                  <p className="text-sm text-ink/70/70">Strengthen the team’s ability to stay grounded, adaptive, and emotionally aware under pressure.</p>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2 flex justify-center">
              <LazyImage 
                src="/6-essential-skills-DETAILED.webp" 
                alt="FIVE LENS People Development: 6 Essential Team Skills" 
                className="w-full max-w-lg object-contain drop-shadow-2xl mix-blend-multiply"
              />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};
